import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductCard from '../../components/base/ProductCard';
import Button from '../../components/base/Button';
import { fortniteProducts, featuredProducts } from '../../mocks/fortniteProducts';
import { useCart } from '../../hooks/useCart';

export default function HomePage() {
  const { addToCart, getTotalItems } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleAddToCart = (productId: string) => {
    const allProducts = [...fortniteProducts, ...featuredProducts];
    const product = allProducts.find(p => p.id === productId);
    if (product) {
      addToCart({
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.image
      });
    }
  };

  const categories = [
    { id: 'all', name: 'All Products', icon: 'ri-apps-line' },
    { id: 'currency', name: 'V-Bucks', icon: 'ri-coin-line' },
    { id: 'battlepass', name: 'Battle Pass', icon: 'ri-trophy-line' },
    { id: 'subscription', name: 'Crew', icon: 'ri-vip-crown-line' },
    { id: 'giftcard', name: 'Gift Cards', icon: 'ri-gift-line' }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? fortniteProducts 
    : fortniteProducts.filter(product => product.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-900">
      <Header cartItemsCount={getTotalItems()} />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/07949e2d47d6379f0adccaf07243df69.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Level Up Your <span className="text-purple-400">Gaming</span> Experience
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Discover the ultimate gaming marketplace with exclusive deals, instant delivery, and premium gaming content for all platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <i className={`${category.icon} text-sm`}></i>
                <span className="text-sm">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Banner */}
      <section className="py-8 bg-gradient-to-r from-yellow-400 to-orange-500">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-black mb-2">🔥 Limited Time Offer</h2>
              <p className="text-black/80">Get up to 20% off on all V-Bucks packages!</p>
            </div>
            <div className="text-right">
              <div className="text-black font-bold text-lg">Ends in: 2d 14h 32m</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Products */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white">
              {selectedCategory === 'all' ? 'Fortnite Products' : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <Button variant="outline" size="sm">
              View all
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                {...product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Gift Cards */}
      <section className="py-12 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white">Epic Games Gift Cards</h2>
            <Button variant="outline" size="sm">
              View all
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProducts.map((product) => (
              <ProductCard
                key={product.id}
                {...product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Secure & Safe</h3>
              <p className="text-gray-400">All transactions are protected with industry-standard encryption</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Instant Delivery</h3>
              <p className="text-gray-400">Get your codes immediately after successful payment</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">24/7 Support</h3>
              <p className="text-gray-400">Our team is here to help you anytime you need assistance</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
